import {
  AuthConfig,
  capitalize,
  cn,
  pluralize,
  queries,
  useAuthConfig,
  useDebounceState,
  ValidControl,
} from "@theprelude/core";
import {
  CheckmarkIcon,
  CloseIcon,
  ControlIcon,
  ExceptionIcon,
  IconButton,
  Input,
  SearchIcon,
  ServerIcon,
  ToggleButton,
  Truncate,
  UserIcon,
} from "@theprelude/ds";
import { PartnerEndpoints } from "@theprelude/sdk";
import { useMemo, useState } from "react";
import { z } from "zod";
import { DefaultOverlay } from "@/components/default-overlay";
import { DefaultTooltip } from "@/components/default-tooltip";
import { getNamedInstance } from "@/components/filter-form/available-filters";
import { Highlight } from "@/components/highlight";
import { createQuery } from "@/lib/create-query";
import { fuzzyFilter } from "@/lib/fuzzy-filters";
import { useEndpointControls } from "../endpoint-tables/scm-columns";

const overlay = "scm-endpoint";

export const scmEndpointSchema = z.tuple([
  z.literal(overlay),
  z.object({
    id: z.string(),
  }),
]);

export type SCMEndpointSchema = NonNullable<
  z.infer<typeof scmEndpointSchema>[1]
>;

type FormattedPartnerEndpoints = Omit<PartnerEndpoints, "controls"> & {
  controls: string[];
};

const scmEndpointQuery = (authConfig: AuthConfig, id: string) =>
  createQuery({
    queries: [
      queries.scm.endpoints(authConfig, {
        query: {
          filter: {
            endpoint_id: id,
          },
        },
      }),
    ],
    queryFn: (endpointsData) => {
      const endpoint = endpointsData.data[0];

      if (!endpoint) {
        throw new Error("Endpoint not found");
      }

      return endpoint;
    },
  });

interface TransformedDataItem {
  label: string;
  value: string | number | boolean | null | (string | number | boolean)[];
  groupLabel: string;
}

const formatLabel = (text: string): string => {
  const normalized = text.replaceAll("_", " ").replaceAll("normalized ", "");
  const avEdrNormalized = normalized
    .replaceAll(/\bav\b/gi, "AV")
    .replaceAll(/\bedr\b/gi, "EDR");
  return capitalize(avEdrNormalized);
};

const formatValue = (value: unknown): string => {
  if (value === null) {
    return "Unavailable";
  }
  if (Array.isArray(value)) {
    return value.join(", ");
  }
  if (typeof value === "boolean") {
    return value ? "True" : "False";
  }
  return String(value);
};

const groupByLabel = (
  data: TransformedDataItem[],
): Record<string, TransformedDataItem[]> => {
  const grouped = data.reduce(
    (acc, item) => {
      const group = item.groupLabel;
      const groupItems = acc[group] || [];
      groupItems.push(item);
      acc[group] = groupItems;
      return acc;
    },
    {} as Record<string, TransformedDataItem[]>,
  );

  Object.keys(grouped).forEach((group) => {
    const items = grouped[group];
    if (items) {
      items.sort((a, b) => a.label.localeCompare(b.label));
    }
  });

  return grouped;
};

function getFormattedEndpoint(
  endpoint: PartnerEndpoints,
  accountControls: ValidControl[],
): FormattedPartnerEndpoints {
  const formattedEndpoint = {
    ...endpoint,
    controls: endpoint.controls.map((c) => {
      const instance = endpoint.instances.find((i) => i.control === c);
      const control = accountControls.find(
        (c) => instance?.instance_id === c.instance_id,
      );

      return getNamedInstance(
        instance && control
          ? { ...control, id: instance.control }
          : control || undefined,
      );
    }),
  };
  return formattedEndpoint;
}

const transformEndpointData = (
  endpoint: PartnerEndpoints,
  accountControls: ValidControl[],
  searchValue: string,
  hideUnavailable: boolean,
): Record<string, TransformedDataItem[]> => {
  const transformedData: TransformedDataItem[] = [];

  transformedData.push({
    label: "Exceptions",
    value: `${endpoint.excepted.length} ${pluralize(endpoint.excepted.length, "exception")}`,
    groupLabel: "",
  });

  const excludedTopKeys = new Set([
    "instances",
    "excepted",
    "failures",
    "groups",
    "sources",
  ]);

  const formattedEndpoint = getFormattedEndpoint(endpoint, accountControls);

  Object.entries(formattedEndpoint).forEach(([key, value]) => {
    if (
      !excludedTopKeys.has(key) &&
      (typeof value !== "object" || value === null || Array.isArray(value)) &&
      (!hideUnavailable || value !== null)
    ) {
      transformedData.push({
        label: formatLabel(key),
        value: formatValue(value),
        groupLabel: "",
      });
    }
  });

  const excludedControlKeys = new Set([
    "control",
    "category",
    "hostname",
    "serial_number",
    "cloud_account_id",
    "cloud_instance_id",
    "mac_addresses",
  ]);

  endpoint.instances.forEach((instance) => {
    const control = accountControls.find(
      (c) => instance.instance_id === c.instance_id,
    );
    const controlName = getNamedInstance(
      control ? { ...control, id: instance.control } : undefined,
    );

    Object.entries(instance).forEach(([key, value]) => {
      if (
        !excludedControlKeys.has(key) &&
        ((typeof value !== "object" && value !== null) ||
          (!hideUnavailable && value === null))
      ) {
        transformedData.push({
          label: `${controlName} ${control?.name ? `(${control?.name})` : ""} ${formatLabel(key)}`,
          value: formatValue(value),
          groupLabel: `${controlName} ${control?.name ? `(${control?.name})` : ""}`,
        });
      }
    });
  });

  if (searchValue) {
    const search = searchValue.toLowerCase();
    return groupByLabel(
      transformedData.filter(
        ({ label, value }) =>
          fuzzyFilter(label, search) || fuzzyFilter(String(value), search),
      ),
    );
  }

  return groupByLabel(transformedData);
};

const sortByConnected =
  (endpoint: PartnerEndpoints) => (a: ValidControl, b: ValidControl) => {
    const isConnected = (control: ValidControl) =>
      endpoint.instances.some((i) => i.control === control.id) ? 1 : 0;
    return isConnected(b) - isConnected(a);
  };

function SCMEndpointContent({ id }: SCMEndpointSchema) {
  const authConfig = useAuthConfig();
  const { data: endpoint } = scmEndpointQuery(
    authConfig,
    id,
  ).useSuspenseQuery();
  const searchTerm = useDebounceState("");
  const [hideUnavailable, setHideUnavailable] = useState(true);
  const accountControls = useEndpointControls();
  const controls = accountControls.reduce<ValidControl[]>((acc, control) => {
    const controlIds = acc.map((a) => a.id);
    if (controlIds.includes(control.id)) {
      return acc;
    }
    return [...acc, control];
  }, []);

  const filteredData = useMemo(() => {
    return transformEndpointData(
      endpoint,
      accountControls,
      searchTerm.debouncedValue,
      hideUnavailable,
    );
  }, [endpoint, accountControls, searchTerm.debouncedValue, hideUnavailable]);

  return (
    <div className="flex h-full flex-col gap-4 overflow-hidden p-4 pb-0">
      <div className="flex items-center gap-2">
        <ServerIcon className="text-secondary-foreground" />
        {endpoint.hostname ? (
          <span className="text-2xl">{endpoint.hostname}</span>
        ) : (
          <span className="text-lg text-secondary-foreground">
            No hostname available
          </span>
        )}
      </div>
      <div className="flex w-full flex-wrap items-center gap-2">
        {controls.sort(sortByConnected(endpoint)).map((control) => {
          const isConnected = endpoint.instances.find(
            (i) => i.control === control.id,
          );
          return (
            <DefaultTooltip
              key={control.id}
              content={getNamedInstance(control)}
            >
              <div
                className={cn(
                  "flex items-center gap-1 rounded border border-stroke-input px-3 py-2.5",
                  isConnected
                    ? "text-status-green"
                    : "text-secondary-foreground",
                )}
              >
                {isConnected ? <CheckmarkIcon /> : <CloseIcon />}
                <ControlIcon
                  control={control.id}
                  className={isConnected ? "size-6" : "size-4"}
                />
              </div>
            </DefaultTooltip>
          );
        })}
      </div>
      <Input
        wrapperClassName="h-9"
        value={searchTerm.value}
        onChange={(e) => {
          searchTerm.setDelayed(e.target.value);
        }}
        before={<SearchIcon className="text-secondary-foreground" />}
        after={
          <IconButton
            icon={<CloseIcon />}
            onClick={() => {
              searchTerm.setImmediate("");
            }}
          />
        }
        placeholder="Search this view"
      />
      <div className="flex items-center justify-start gap-1">
        <ToggleButton
          checked={hideUnavailable}
          onChange={() => setHideUnavailable((state) => !state)}
        />
        <span>Hide empty values</span>
      </div>
      <div className="flex flex-col gap-4 overflow-y-auto pb-12">
        {Object.entries(filteredData).map(([group, items]) => (
          <div key={group} className="flex flex-col gap-2">
            {group && (
              <h3 className="text-secondary-foreground text-xs">{group}</h3>
            )}
            {items.map((item, index) => (
              <div
                className="flex max-w-[800px] flex-row gap-4 border-stroke-table border-b py-2 text-sm"
                key={index}
              >
                <Highlight
                  highlight={searchTerm.debouncedValue}
                  text={`${item.label}:`}
                  className="w-full max-w-[450px]"
                />
                <div className="flex items-center gap-1">
                  {item.label === "Exceptions" ? (
                    <ExceptionIcon className="text-secondary-foreground" />
                  ) : item.label === "Hostname" ? (
                    <UserIcon className="text-secondary-foreground" />
                  ) : null}
                  <Truncate text={String(item.value)}>
                    <Highlight
                      highlight={searchTerm.debouncedValue}
                      text={String(item.value)}
                      className={cn(
                        item.value === "Unavailable" &&
                          "text-secondary-foreground",
                      )}
                    />
                  </Truncate>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export function SCMEndpointOverlay({ id }: SCMEndpointSchema) {
  return (
    <DefaultOverlay title="Endpoint Details">
      <SCMEndpointContent id={id} />
    </DefaultOverlay>
  );
}
